import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./voice-recovery.css";
import type { AutomationStatsDto, Locale, ProfileFactDto, ProfileResponse } from "../shared/dto";
import { profileResearchIsWorking, type ProfileResearchState } from "../shared/profile-research";
import { AutomationTimingEstimate } from "./AutomationTimingEstimate";
import { formatMediumDateTime } from "./date-format";
import { drawOrbRing, transcribeLive } from "./VoiceableInput";
import { moveWalkCursorOrb } from "./doc/walk";
import { notifyReadonlyBlocked, readonlyBlocksVoice } from "./readonly";
import {
  WALK_MAX_RECORDING_SECONDS,
  classifySendFailure,
  classifyStartFailure,
  createVoiceActivityGuard,
  voiceFailureKeepsRecording,
  voiceFailureMessageKey,
  voiceFailureRetryKey,
  worthTranscribing,
  type VoiceActivityGuard,
  type VoiceFailure
} from "./voice-recording";
import { dropVoiceDraft, readVoiceDraft, writeVoiceDraft } from "./voice-draft";
import { WorkingAvatar } from "./WorkingAvatar";
import { actionIssueFrom, actionIssueFromThrown, actionIssueLocaleKey, type ActionIssue } from "./action-error";

type LoadState = { kind: "loading" } | { kind: "building" } | { kind: "error" } | { kind: "ready"; data: ProfileResponse };

const CATEGORY_ORDER: ProfileFactDto["category"][] = ["identity", "business", "audience", "offer", "voice", "proof", "other"];
// Branch hues follow the brand: blue/cyan family for AI-gathered knowledge,
// green for proof; no purple.
const BRANCH_COLORS = ["#4a90ff", "#22d3ee", "#2dd4bf", "#8fb8ff", "#38bdf8", "#34d399", "#7dd3fc"];

interface TracePoint { t: number; x: number; y: number; fact?: string }
interface ProfileRecordingSummary { id: string; text?: string | null; createdAt?: string; state: string }

export function hasActiveProfileMapApply(recordings: ProfileRecordingSummary[]): boolean {
  return recordings.some((recording) => recording.state === "applying");
}

export function didProfileMapApplyFinish(
  previous: ProfileRecordingSummary[],
  next: ProfileRecordingSummary[]
): boolean {
  const nextStates = new Map(next.map((recording) => [recording.id, recording.state]));
  return previous.some((recording) => recording.state === "applying" && nextStates.get(recording.id) === "applied");
}

// The q1 gate reuses the profile language without duplicating the profile
// editor. WALK-WB owns the confirmation control itself; this additive message
// explains why the questionnaire cannot be generated before that confirmation.
// Walk 2026-07-31: one button that REALLY navigates to the profile step.
// The old pair ("Zpět na přehled a profil" = plain close, "Profil už jsem
// potvrdil" = silent reload) felt dead in Pavel's live test.
export function ProfileConfirmationGate(props: {
  t: (key: string) => string;
  onOpenProfile(): void;
}) {
  return (
    <section className="profile-confirmation-gate" aria-labelledby="profile-confirmation-gate-title">
      <p className="eyebrow blue-eyebrow">{props.t("profile.questionnaireGate.eyebrow")}</p>
      <h1 id="profile-confirmation-gate-title">{props.t("profile.questionnaireGate.title")}</h1>
      <p>{props.t("profile.questionnaireGate.copy")}</p>
      <ol>
        <li>{props.t("profile.questionnaireGate.review")}</li>
        <li>{props.t("profile.questionnaireGate.correct")}</li>
        <li>{props.t("profile.questionnaireGate.confirm")}</li>
      </ol>
      <div className="profile-confirmation-gate-actions">
        <button className="primary-button" type="button" onClick={props.onOpenProfile}>
          {props.t("profile.questionnaireGate.openProfile")}
        </button>
      </div>
    </section>
  );
}

// The live profile is a mind map (Pavel: the compose lessonmap style is far
// clearer than two columns of text), with a voice walk on top: the client
// turns on recording, points with the mouse and talks; we keep the audio and
// the pointer trail.
export function ProfileView(props: {
  csrfToken: string;
  locale: Locale;
  clientName: string;
  confirmed: boolean;
  /** F145: the server's ONE reading of the initial research. */
  research: ProfileResearchState;
  profilePhotoUrl?: string | null;
  t: (key: string) => string;
  onChanged: () => Promise<void>;
}) {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [mapAppliedNotice, setMapAppliedNotice] = useState(false);

  const load = useCallback(async (showLoading = true): Promise<boolean> => {
    if (showLoading) setState({ kind: "loading" });
    try {
      const response = await fetch("/api/profile", { credentials: "include" });
      if (response.status === 409) {
        if (showLoading) setState({ kind: "building" });
        return false;
      }
      if (!response.ok) throw new Error("profile_load_failed");
      setState({ kind: "ready", data: await response.json() as ProfileResponse });
      return true;
    } catch {
      if (showLoading) setState({ kind: "error" });
      return false;
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleMapApplied = useCallback(async () => {
    if (await load(false)) setMapAppliedNotice(true);
  }, [load]);

  const branches = useMemo(() => {
    if (state.kind !== "ready") return [];
    const grouped = new Map<ProfileFactDto["category"], ProfileFactDto[]>();
    for (const fact of state.data.facts) {
      if (!fact.included) continue;
      grouped.set(fact.category, [...(grouped.get(fact.category) ?? []), fact]);
    }
    return CATEGORY_ORDER.filter((category) => grouped.has(category))
      .map((category, index) => ({ category, color: BRANCH_COLORS[index % BRANCH_COLORS.length], facts: grouped.get(category)! }));
  }, [state]);

  // Facts removed in this session disappear locally with an animation; no
  // profile reload, no scroll jump (Pavel's feedback).
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const visibleBranches = branches
    .map((branch) => ({ ...branch, facts: branch.facts.filter((fact) => fact.action !== "irrelevant" && !removedIds.has(fact.id)) }))
    .filter((branch) => branch.facts.length > 0);

  if (state.kind === "loading") return <section className="m4-state" aria-live="polite"><div className="loading-pulse" /><p>{props.t("common.loading")}</p></section>;
  // While the dossier is still building (or research failed), the profile must
  // NOT be a dead end: the client can record the basics right away - the walk
  // recorder works without a dossier (Pavel's walk: empty profile showed
  // nothing to do).
  if (state.kind === "building") {
    return <ProfileBuildingState csrfToken={props.csrfToken} t={props.t} locale={props.locale} research={props.research} profilePhotoUrl={props.profilePhotoUrl} />;
  }
  if (state.kind === "error") return <section className="m4-empty"><p className="error-message">{props.t("profile.loadError")}</p><button className="secondary-button compact-button" type="button" onClick={() => void load()}>{props.t("common.retry")}</button></section>;

  return (
    <section className="m4-view profile-view" aria-labelledby="profile-view-title">
      <header className="m4-heading">
        <p className="eyebrow blue-eyebrow">{props.t("profile.eyebrow")}</p>
        <h1 id="profile-view-title">{props.t("rail.steps.profile")}</h1>
        {/* K08, one situation over: `lead2` announces a finished profile
            ("vytvořili jsme tvůj profil"), and with every fact excluded the
            card below it says we found nothing. The perex reads the SAME fact
            as the state under it. */}
        <p className="lead">{props.t(visibleBranches.length === 0 ? "profile.research.emptyLead" : "profile.lead2")}</p>
      </header>
      {mapAppliedNotice && (
        <div className="profile-map-applied-banner" role="status" aria-live="polite">
          <span aria-hidden="true">{"✓"}</span>
          <p>{props.t("profile.recordings.completedBanner")}</p>
        </div>
      )}
      <ProfileWalk
        csrfToken={props.csrfToken}
        t={props.t}
        locale={props.locale}
        locked={props.confirmed}
        // F145: with no map to point at, the recorder must ask for the
        // INTRODUCTION, not narrate a walk over branches that are not there.
        // A dossier that exists and holds nothing the client can see is the
        // same situation as a run that found nothing, so it gets the same
        // recorder and the same words.
        variant={visibleBranches.length === 0 ? "basics" : "map"}
        onMapApplied={handleMapApplied}
        extra={props.confirmed ? undefined : <MapReset csrfToken={props.csrfToken} t={props.t} onDeleted={async () => { await load(); }} />}
      >
        {(onPointer, walkActive) => (
          visibleBranches.length === 0 ? (
            // Honest empty state (Pavel 2026-08-13): when the research found
            // nothing, say so and ask the client to introduce themselves. The
            // SAME two keys the `empty` research state renders - two screens
            // that are indistinguishable to the client must not be written
            // twice, or they drift into saying different things.
            <div className="profile-empty-research" data-profile-state="empty">
              <h2>{props.t("profile.research.emptyTitle")}</h2>
              <p>{props.t("profile.research.emptyCopy")}</p>
              <p>{props.t("profile.research.emptyRecord")}</p>
            </div>
          ) : (
          <ProfileMap
            clientName={props.clientName}
            branches={visibleBranches}
            csrfToken={props.csrfToken}
            t={props.t}
            onPointer={onPointer}
            walkActive={walkActive}
            locked={props.confirmed}
            onFactRemoved={(factId) => setRemovedIds((current) => new Set([...current, factId]))}
            onFactRestored={(factId) => setRemovedIds((current) => { const next = new Set(current); next.delete(factId); return next; })}
          />
          )
        )}
      </ProfileWalk>
      <ProfileConfirmation
        confirmed={props.confirmed}
        csrfToken={props.csrfToken}
        t={props.t}
        onChanged={props.onChanged}
      />
    </section>
  );
}

export function ProfileConfirmation(props: {
  confirmed: boolean;
  csrfToken: string;
  t: (key: string) => string;
  onChanged: () => Promise<void>;
}) {
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  // 🔴 F147: the confirmation is the ONE click the whole Buddy brief stands on,
  // and it used to answer every failure with the same sentence ("Profil se
  // nepodařilo potvrdit. Zkus to prosím znovu.") - a repeat that cannot work
  // when the refusal is a state conflict or an expired session. The server
  // NAMES its refusals, so the client prints that name (the fix2-chyby law).
  const [issue, setIssue] = useState<ActionIssue>("generic");
  const confirmed = props.confirmed || state === "done";

  async function confirm() {
    setState("sending");
    try {
      const response = await fetch("/api/client-steps/profile/confirm", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken,
          "Idempotency-Key": crypto.randomUUID()
        },
        body: "{}"
      });
      if (!response.ok) {
        setIssue(await actionIssueFrom(response));
        setState("error");
        return;
      }
      const body = await response.json() as { ok?: boolean };
      if (body.ok !== true) {
        setIssue("generic");
        setState("error");
        return;
      }
      setState("done");
      // The fact is stored; a failed REFETCH is not a failed confirmation, so
      // the phase stays `done` and the page keeps saying what really happened.
      await props.onChanged();
    } catch {
      setIssue(actionIssueFromThrown());
      setState("error");
    }
  }

  return (
    <div
      className={`section-action profile-confirmation${confirmed ? " is-confirmed" : ""}${state === "done" ? " is-fresh" : ""}`}
      data-profile-confirmed={confirmed}
      aria-live="polite"
    >
      {confirmed ? (
        <>
          <button className="profile-confirmed-control" type="button" disabled>
            <span aria-hidden="true">{"✓"}</span>
            {props.t("profile.confirmation.done")}
          </button>
          <p className="section-action-note">{props.t("profile.confirmation.locked")}</p>
        </>
      ) : (
        <button className="primary-button" type="button" disabled={state === "sending"} onClick={() => void confirm()}>
          {props.t(state === "sending" ? "profile.confirmation.sending" : "profile.confirmation.button")}
        </button>
      )}
      {state === "error" && (
        <p className="error-message" role="alert">
          {props.t(issue === "generic" ? "profile.confirmation.error" : actionIssueLocaleKey(issue))}
        </p>
      )}
    </div>
  );
}

// A missing dossier keeps the full scaffold and live recorder so the client
// can act now. The copy names WHICH of four situations this is. Exported for
// the render test.
export function ProfileBuildingState(props: {
  csrfToken: string;
  t: (key: string) => string;
  locale: Locale;
  /** F145: the server's ONE reading of the initial research. */
  research: ProfileResearchState;
  profilePhotoUrl?: string | null;
}) {
  // F145: FOUR states, because there are four situations, and the two the old
  // pair of booleans could not tell apart are the two that lied.
  //
  //   running  the machine really is on it        -> "právě vznikají"
  //   delayed  a job nobody is running any more   -> ours, and we say so
  //   empty    it ran and found nothing           -> Pavel's ask: the mic
  //   none     nothing was ever queued            -> the mic, no excuse needed
  //
  // 🔴 `delayed` deliberately does NOT say "ve zdrojích jsme o tobě nic
  // nenašli". No search finished, so that sentence would be the same class of
  // invention as the promise it replaces - only in the other direction. What
  // is true is that we are late, that we know, and that the microphone works
  // now either way.
  const state = props.research;
  const copyKey = (suffix: "Lead" | "Title" | "Copy" | "Record"): string => `profile.research.${state}${suffix}`;
  return (
    <section className="m4-view profile-view" aria-labelledby="profile-view-title">
      <header className="m4-heading">
        <p className="eyebrow blue-eyebrow">{props.t("profile.eyebrow")}</p>
        <h1 id="profile-view-title">{props.t("rail.steps.profile")}</h1>
        {/* K08: `profile.lead2` announces a finished profile ("vytvořili jsme
            tvůj profil"), and 300 px below it the empty state said "Profil
            zatím nemáme". The perex reads the SAME fact as the empty state
            underneath it, so the screen says one thing. */}
        <p className="lead">{props.t(copyKey("Lead"))}</p>
      </header>
      <ProfileWalk csrfToken={props.csrfToken} t={props.t} locale={props.locale} variant="basics">
        {() => (
          <div className="profile-empty-research" data-profile-state={state}>
            {/* F53: the ONE working marker, and it is drawn only while the team
                really is working. `profileResearchIsWorking` is the same
                reading the copy above uses. */}
            {profileResearchIsWorking(state) && <WorkingAvatar size="card" profilePhotoUrl={props.profilePhotoUrl} />}
            <h2>{props.t(copyKey("Title"))}</h2>
            <p>{props.t(copyKey("Copy"))}</p>
            {/* The line about the microphone is per state too: "nemusíš čekat"
                is right while something is coming and hollow when nothing is. */}
            <p>{props.t(copyKey("Record"))}</p>
          </div>
        )}
      </ProfileWalk>
    </section>
  );
}

// ---- voice walk recorder (audio + pointer trail) ----

const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];

function ProfileWalk(props: {
  csrfToken: string;
  t: (key: string) => string;
  // F116: the recording list below dates every note, so the walk needs the
  // reader's language. Required, not defaulted: a default is how "cs-CZ" got
  // hardcoded here in the first place.
  locale: Locale;
  locked?: boolean;
  // "map" narrates walking the existing map; "basics" (building state) asks
  // for the first facts instead - there is no map below yet to point at.
  variant?: "map" | "basics";
  extra?: React.ReactNode;
  onMapApplied?: () => Promise<void>;
  children: (onPointer: (point: Omit<TracePoint, "t">) => void, walkActive: boolean) => React.ReactNode;
}) {
  const [phase, setPhase] = useState<"idle" | "recording" | "sending" | "sent" | "error" | "resume">("idle");
  const [elapsed, setElapsed] = useState(0);
  // F98: the cause survives the catch, and a note that failed to DELIVER is
  // held for a real re-send instead of being discarded with the phase change.
  const [failure, setFailure] = useState<VoiceFailure | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const startingRef = useRef(false);
  const heldRef = useRef<{ blob: Blob; type: string; transcript: string | null; attachmentId: string | null } | null>(null);
  const heldTraceRef = useRef<TracePoint[]>([]);
  const guardRef = useRef<VoiceActivityGuard | null>(null);
  const [recordings, setRecordings] = useState<Array<{ id: string; text: string | null; createdAt: string; state: string }>>([]);
  const [mapApplyStats, setMapApplyStats] = useState<AutomationStatsDto | undefined>();
  const [busyRecording, setBusyRecording] = useState<string | null>(null);
  const [recordingError, setRecordingError] = useState<ActionIssue | null>(null);
  const recordingsRef = useRef<ProfileRecordingSummary[]>([]);
  const requestActiveRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadRecordings = useCallback(async () => {
    if (requestActiveRef.current) return;
    requestActiveRef.current = true;
    try {
      const response = await fetch("/api/profile/feedback", { credentials: "include" });
      if (!response.ok) return;
      const body = await response.json() as {
        recordings: typeof recordings;
        automationStats?: AutomationStatsDto;
      };
      if (!mountedRef.current) return;
      const next = body.recordings ?? [];
      const completed = didProfileMapApplyFinish(recordingsRef.current, next);
      recordingsRef.current = next;
      setRecordings(next);
      setMapApplyStats(body.automationStats);
      if (completed) await props.onMapApplied?.();
    } catch { /* list is best-effort */ }
    finally { requestActiveRef.current = false; }
  }, [props.onMapApplied]);

  useEffect(() => {
    void loadRecordings();
  }, [loadRecordings]);

  useEffect(() => {
    if (!hasActiveProfileMapApply(recordings)) return;
    let cancelled = false;
    let timer = 0;
    const schedule = () => {
      timer = window.setTimeout(() => {
        void loadRecordings().finally(() => { if (!cancelled) schedule(); });
      }, 3000);
    };
    schedule();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [loadRecordings, recordings]);

  async function deleteRecording(id: string) {
    setBusyRecording(id);
    setRecordingError(null);
    try {
      const response = await fetch(`/api/profile/feedback/${encodeURIComponent(id)}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "X-Launchpad-CSRF": props.csrfToken }
      });
      if (!response.ok) {
        setRecordingError(await actionIssueFrom(response));
        return;
      }
      await loadRecordings();
    } catch {
      setRecordingError(actionIssueFromThrown());
    } finally {
      setBusyRecording(null);
    }
  }

  async function applyRecording(id: string) {
    setBusyRecording(id);
    setRecordingError(null);
    try {
      const response = await fetch(`/api/profile/feedback/${encodeURIComponent(id)}/apply`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Launchpad-CSRF": props.csrfToken, "Idempotency-Key": crypto.randomUUID() }
      });
      if (!response.ok) {
        setRecordingError(await actionIssueFrom(response));
        return;
      }
      await loadRecordings();
    } catch {
      setRecordingError(actionIssueFromThrown());
    } finally {
      setBusyRecording(null);
    }
  }
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const cancelledRef = useRef(false);
  const traceRef = useRef<TracePoint[]>([]);
  const startedAtRef = useRef(0);
  const lastSampleRef = useRef(0);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const ringRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef(0);

  useEffect(() => {
    if (!props.locked || recorderRef.current?.state !== "recording") return;
    cancelledRef.current = true;
    recorderRef.current.stop();
  }, [props.locked]);

  useEffect(() => {
    // The orb breathes in every state and follows the voice while recording
    // (Pavel: the animation must stay alive during the recording too).
    let active = true;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const animate = (time: number) => {
      if (!active) return;
      let bins: Uint8Array | null = null;
      if (phase === "recording" && analyserRef.current) {
        const data = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(data);
        bins = data;
      }
      if (ringRef.current) drawOrbRing(ringRef.current, { bins, tick: reduceMotion ? 0 : time, level: 0.4, live: phase === "recording" });
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      active = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== "recording") return;
    const timer = window.setInterval(() => {
      const guard = guardRef.current;
      const verdict = guard ? guard.sample(Date.now()) : "continue";
      setElapsed(guard ? guard.elapsedSeconds() : 0);
      if (verdict === "silence_stop") {
        setNotice(props.t("voice.failure.silenceStopped"));
        stopWalkRecording(!guard?.hadVoice());
      } else if (verdict === "limit_stop") {
        setNotice(props.t("voice.failure.limitStopped"));
        stopWalkRecording(false);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [phase, props.t]);

  // Leaving mid-recording, or while a finished note is still unsent, loses it.
  useEffect(() => {
    if (phase !== "recording" && heldRef.current === null) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [phase]);

  // A recording the browser lost (tab closed, crash) is OFFERED back, never
  // re-sent on its own.
  useEffect(() => {
    if (props.locked) return;
    let live = true;
    void readVoiceDraft("profile", "walk").then((record) => {
      if (!live || !record || heldRef.current) return;
      heldRef.current = {
        blob: new Blob(record.chunks, { type: record.mime }),
        type: record.mime,
        transcript: record.transcript,
        attachmentId: null
      };
      heldTraceRef.current = [];
      setPhase("resume");
    });
    return () => { live = false; };
  }, [props.locked]);

  const onPointer = useCallback((point: Omit<TracePoint, "t">) => {
    if (recorderRef.current?.state !== "recording") return;
    const now = performance.now();
    if (now - lastSampleRef.current < 120 || traceRef.current.length >= 5500) return;
    lastSampleRef.current = now;
    traceRef.current.push({ t: Math.round(now - startedAtRef.current), ...point });
  }, []);

  function teardown() {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
    analyserRef.current = null;
    void audioContextRef.current?.close().catch(() => undefined);
    audioContextRef.current = null;
  }

  function persistWalkDraft(state: "recording" | "unsent", mime: string, chunks: Blob[], transcript: string | null) {
    if (chunks.length === 0) return;
    const now = Date.now();
    void writeVoiceDraft({
      id: "profile:walk",
      surface: "profile",
      targetId: "walk",
      mime,
      chunks,
      state,
      transcript,
      startedAt: now,
      updatedAt: now
    });
  }

  function forgetWalkDraft() {
    heldRef.current = null;
    void dropVoiceDraft("profile", "walk");
  }

  function failWalk(reason: VoiceFailure) {
    setFailure(reason);
    setPhase("error");
  }

  function stopWalkRecording(cancelled: boolean) {
    cancelledRef.current = cancelled;
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
  }

  async function start() {
    // Impersonation preview: refuse before any recording effort is invested.
    if (readonlyBlocksVoice()) {
      notifyReadonlyBlocked();
      return;
    }
    // Two streams from a double click leave one of them without the device.
    if (startingRef.current || recorderRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      failWalk("unsupported");
      return;
    }
    startingRef.current = true;
    setNotice(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MIME_CANDIDATES.find((candidate) => MediaRecorder.isTypeSupported(candidate)) ?? "";
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];
      traceRef.current = [];
      cancelledRef.current = false;
      startedAtRef.current = performance.now();
      lastSampleRef.current = 0;
      forgetWalkDraft();
      setFailure(null);
      setElapsed(0);
      let analyser: AnalyserNode | null = null;
      try {
        const context = new AudioContext();
        analyser = context.createAnalyser();
        analyser.fftSize = 256;
        context.createMediaStreamSource(stream).connect(analyser);
        audioContextRef.current = context;
        analyserRef.current = analyser;
      } catch { /* visual only, and the guard fails open without it */ }
      guardRef.current = createVoiceActivityGuard({
        analyser,
        maxSeconds: WALK_MAX_RECORDING_SECONDS,
        startedAt: Date.now()
      });
      recorder.ondataavailable = (event) => {
        if (event.data.size === 0) return;
        chunksRef.current.push(event.data);
        persistWalkDraft("recording", recorder.mimeType || mime || event.data.type, chunksRef.current, null);
      };
      recorder.onstop = () => {
        const chunks = chunksRef.current;
        const type = recorder.mimeType || mime || chunks[0]?.type || "audio/webm";
        const hadVoice = guardRef.current?.hadVoice() ?? true;
        teardown();
        if (cancelledRef.current || chunks.length === 0) {
          forgetWalkDraft();
          setPhase("idle");
          return;
        }
        const blob = new Blob(chunks, { type });
        // Never buy a transcript of silence.
        if (!worthTranscribing({ hadVoice, byteSize: blob.size })) {
          forgetWalkDraft();
          failWalk("empty");
          return;
        }
        heldRef.current = { blob, type, transcript: null, attachmentId: null };
        heldTraceRef.current = traceRef.current.slice();
        persistWalkDraft("unsent", type, chunks, null);
        void send();
      };
      recorder.start(1000);
      setPhase("recording");
    } catch (error) {
      teardown();
      failWalk(classifyStartFailure(error));
    } finally {
      startingRef.current = false;
    }
  }

  // Delivery of the HELD recording. Each step that already succeeded is kept,
  // so a re-send never pays Scribe twice.
  async function send() {
    const held = heldRef.current;
    if (!held) return;
    setFailure(null);
    setPhase("sending");
    try {
      // Scribe transcribes right away; the text is the primary carrier and the
      // audio attachment is a best-effort archive (R2 may still be off).
      if (!held.transcript) {
        const text = await transcribeLive(held.blob, held.type, props.csrfToken);
        // An empty transcript means no speech, not a failed transcription:
        // calling it a failure would offer to send the same silence again.
        if (!text) throw new Error("no_speech");
        held.transcript = text;
        persistWalkDraft("unsent", held.type, [held.blob], text);
      }
      if (!held.attachmentId) {
        try {
          const form = new FormData();
          form.set("scope", "profile_voice");
          // 🔴 Same cost guard as the sales-page walk: an archive posted
          // without its transcript re-enters the paid queue and Scribe bills
          // the same minutes twice.
          if (held.transcript) form.set("transcript", held.transcript);
          form.set("file", new File([held.blob], `profile-walk.${held.type.includes("mp4") ? "m4a" : "webm"}`, { type: held.type }));
          const upload = await fetch("/api/attachments", {
            method: "POST",
            credentials: "include",
            headers: { "X-Launchpad-CSRF": props.csrfToken },
            body: form
          });
          const uploaded = await upload.json() as { attachmentId?: string };
          if (upload.ok && uploaded.attachmentId) held.attachmentId = uploaded.attachmentId;
        } catch { /* archive optional: the transcript is the note */ }
      }
      const response = await fetch("/api/profile/feedback", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Launchpad-CSRF": props.csrfToken, "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ text: held.transcript, attachmentId: held.attachmentId, pointerTrace: heldTraceRef.current })
      });
      if (!response.ok) throw new Error("feedback_failed");
      forgetWalkDraft();
      heldTraceRef.current = [];
      setPhase("sent");
      // Local refresh only: the new recording glides into the list, the page
      // itself never reloads (Pavel's feedback).
      await loadRecordings();
    } catch (error) {
      // A reason that keeps nothing worth sending (no speech) drops the take,
      // so it is never offered back or paid for twice.
      const reason = classifySendFailure(error, navigator.onLine !== false);
      if (!voiceFailureKeepsRecording(reason)) forgetWalkDraft();
      failWalk(reason);
    }
  }

  if (props.locked) {
    return (
      <div className="profile-walk profile-walk-locked">
        <div className="profile-walk-bar" role="status">
          <span className="profile-walk-lock-mark" aria-hidden="true">{"✓"}</span>
          <div className="profile-walk-copy">
            <strong>{props.t("profile.confirmation.done")}</strong>
            <p>{props.t("profile.confirmation.locked")}</p>
          </div>
        </div>
        {recordings.length > 0 && (
          <section className="profile-recordings" aria-label={props.t("profile.recordings.title")}>
            <h2>{props.t("profile.recordings.title")}</h2>
            <ol>
              {recordings.map((item) => (
                <li key={item.id}>
                  <div className="profile-recording-head">
                    <time dateTime={item.createdAt}>{formatMediumDateTime(item.createdAt, props.locale)}</time>
                    {item.state === "applied" && <span className="q-item-chip status-pill pill-done">{props.t("profile.recordings.applied")}</span>}
                    <ProfileRecordingAutomationState state={item.state} stats={mapApplyStats} t={props.t} />
                    {item.state === "review" && <span className="profile-recording-review status-pill pill-client">{props.t("profile.recordings.review")}</span>}
                  </div>
                  {item.text && <p>{item.text}</p>}
                </li>
              ))}
            </ol>
          </section>
        )}
        {props.children(() => undefined, false)}
      </div>
    );
  }

  return (
    <div className={`profile-walk ${phase === "recording" ? "recording" : ""}`}>
      <div className="profile-walk-bar" role="region" aria-label={props.t("profile.walk.title")}>
        <button
          type="button"
          className={`voice-inline-mic profile-walk-orb ${phase === "recording" ? "recording" : ""}`}
          aria-label={phase === "recording" ? props.t("profile.walk.stop") : props.t("profile.walk.start")}
          title={phase === "recording" ? props.t("profile.walk.stop") : props.t("profile.walk.start")}
          disabled={phase === "sending"}
          onClick={() => {
            if (phase === "recording") stopWalkRecording(false);
            else if (phase !== "sending") void start();
          }}
        >
          <canvas ref={ringRef} className="voice-orb-ring" aria-hidden="true" />
          <svg className="voice-orb-mic" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path fill="currentColor" d="M12 14.5a3.3 3.3 0 0 0 3.3-3.3V6.3a3.3 3.3 0 1 0-6.6 0v4.9a3.3 3.3 0 0 0 3.3 3.3Zm5.8-3.3a5.8 5.8 0 0 1-11.6 0H4.4a7.6 7.6 0 0 0 6.7 7.5v2.5h1.8v-2.5a7.6 7.6 0 0 0 6.7-7.5h-1.8Z" />
          </svg>
        </button>
        <div className="profile-walk-copy">
          {phase === "recording" ? (
            <>
              <strong>{props.t("profile.walk.recTitle")} <span className="profile-walk-timer">{formatWalkTime(elapsed)}</span></strong>
              <p>{props.t("profile.walk.recInstruction")}</p>
              <div className="profile-walk-actions">
                <button className="primary-button compact-button" type="button" onClick={() => stopWalkRecording(false)}>{props.t("profile.walk.stop")}</button>
                <button className="quiet-link" type="button" onClick={() => stopWalkRecording(true)}>{props.t("profile.walk.cancel")}</button>
              </div>
            </>
          ) : phase === "sending" ? (
            <>
              <strong>{props.t("voice.transcribing")}</strong>
              <p>{props.t("profile.walk.sendingNote")}</p>
            </>
          ) : phase === "sent" ? (
            <>
              <strong>{props.t("profile.walk.sentTitle")}</strong>
              <p>{props.t("profile.walk.sent")}</p>
              <div className="profile-walk-actions">
                <button className="secondary-button compact-button" type="button" onClick={() => void start()}>{props.t("profile.recordings.another")}</button>
              </div>
            </>
          ) : (
            <>
              <strong>{props.t(props.variant === "basics" ? "profile.walkBasics.title" : "profile.walk.title")}</strong>
              <p>{props.t(props.variant === "basics" ? "profile.walkBasics.explain" : "profile.walk.explain")}</p>
              {props.variant !== "basics" && (
                <ul className="profile-walk-points">
                  <li>{props.t("profile.walk.point1")}</li>
                  <li>{props.t("profile.walk.point2")}</li>
                </ul>
              )}
              {phase === "idle" && (
                <div className="profile-walk-actions">
                  <button
                    className="secondary-button compact-button profile-record-cta"
                    data-profile-record-cta
                    type="button"
                    onClick={() => void start()}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="currentColor" d="M12 14.5a3.3 3.3 0 0 0 3.3-3.3V6.3a3.3 3.3 0 1 0-6.6 0v4.9a3.3 3.3 0 0 0 3.3 3.3Zm5.8-3.3a5.8 5.8 0 0 1-11.6 0H4.4a7.6 7.6 0 0 0 6.7 7.5v2.5h1.8v-2.5a7.6 7.6 0 0 0 6.7-7.5h-1.8Z" />
                    </svg>
                    {props.t("profile.walk.cta")}
                  </button>
                </div>
              )}
              {phase === "error" && failure && (
                <div className="profile-walk-actions">
                  <p className="message error-message" role="alert">{props.t(voiceFailureMessageKey(failure))}</p>
                  {voiceFailureKeepsRecording(failure) && heldRef.current && (
                    <p className="walk-held-note">{props.t("voice.failure.heldNote")}</p>
                  )}
                  <button
                    className="secondary-button compact-button"
                    type="button"
                    onClick={() => (voiceFailureKeepsRecording(failure) && heldRef.current ? void send() : void start())}
                  >
                    {props.t(voiceFailureRetryKey(failure))}
                  </button>
                </div>
              )}
              {phase === "resume" && (
                <div className="profile-walk-actions walk-resume" role="status">
                  <strong>{props.t("voice.failure.resumeTitle")}</strong>
                  <p>{props.t("voice.failure.resumeBody")}</p>
                  <div className="walk-resume-actions">
                    <button className="primary-button compact-button" type="button" onClick={() => void send()}>
                      {props.t("voice.failure.resumeSend")}
                    </button>
                    <button className="quiet-link" type="button" onClick={() => { forgetWalkDraft(); setPhase("idle"); }}>
                      {props.t("voice.failure.resumeDiscard")}
                    </button>
                  </div>
                </div>
              )}
              {notice && <p className="walk-transfer-notice" role="status">{notice}</p>}
            </>
          )}
        </div>
      </div>
      {recordings.length > 0 && (
        <section className="profile-recordings" aria-label={props.t("profile.recordings.title")}>
          <h2>{props.t("profile.recordings.title")}</h2>
          {recordingError && <p className="message error-message" role="alert">{props.t(actionIssueLocaleKey(recordingError))}</p>}
          <ol>
            {recordings.map((item) => (
              <li key={item.id}>
                <div className="profile-recording-head">
                  <time dateTime={item.createdAt}>{formatMediumDateTime(item.createdAt, props.locale)}</time>
                  {item.state === "applied" && <span className="q-item-chip status-pill pill-done">{props.t("profile.recordings.applied")}</span>}
                  <ProfileRecordingAutomationState state={item.state} stats={mapApplyStats} t={props.t} />
                  {item.state === "review" && <span className="profile-recording-review status-pill pill-client">{props.t("profile.recordings.review")}</span>}
                  <span className="profile-recording-actions">
                    {item.state === "new" && item.text && (
                      <button className="quiet-link" type="button" disabled={busyRecording === item.id} onClick={() => void applyRecording(item.id)}>
                        {props.t("profile.recordings.apply")}
                      </button>
                    )}
                    {item.state === "new" && (
                      <button className="quiet-link" type="button" disabled={busyRecording === item.id} onClick={() => void deleteRecording(item.id)}>
                        {props.t("profile.recordings.delete")}
                      </button>
                    )}
                  </span>
                </div>
                {item.text && <p>{item.text}</p>}
              </li>
            ))}
          </ol>
        </section>
      )}
      {props.extra}
      {props.children(onPointer, phase === "recording")}
    </div>
  );
}

export function ProfileRecordingAutomationState(props: {
  state: string;
  stats?: AutomationStatsDto;
  t: (key: string) => string;
}) {
  if (props.state !== "applying") return null;
  return (
    <>
      <span className="profile-recording-applying status-pill pill-team" role="status" data-profile-recording-active>
        <span className="profile-recording-spinner" aria-hidden="true" />
        <span>{props.t("profile.recordings.applying")}</span>
      </span>
      {props.stats && props.stats.sampleSize >= 2 && props.stats.medianMs !== null
        ? <AutomationTimingEstimate stats={props.stats} t={props.t} />
        : <small className="profile-recording-eta-fallback">{props.t("profile.recordings.etaFallback")}</small>}
    </>
  );
}

// "Smazat mapu": for a map built on the wrong person or too little public
// data. Serious in-place confirmation; the wipe imports an empty dossier
// version, then the client rebuilds through recordings.
function MapReset(props: { csrfToken: string; t: (key: string) => string; onDeleted: () => Promise<void> }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  async function wipe() {
    setBusy(true);
    setFailed(false);
    try {
      const response = await fetch("/api/profile/map/delete", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken,
          "Idempotency-Key": crypto.randomUUID()
        },
        body: "{}"
      });
      if (!response.ok) throw new Error("map_delete_failed");
      setConfirming(false);
      await props.onDeleted();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="map-reset">
      <p className="map-reset-tip">
        {props.t("profile.mapReset.tip")}{" "}
        {!confirming && (
          <button className="quiet-link map-reset-open" type="button" onClick={() => setConfirming(true)}>
            {props.t("profile.mapReset.deleteButton")}
          </button>
        )}
      </p>
      {confirming && (
        <div className="map-reset-confirm" role="alertdialog" aria-labelledby="map-reset-title">
          <strong id="map-reset-title">{props.t("profile.mapReset.dialogTitle")}</strong>
          <p>{props.t("profile.mapReset.dialogBody")}</p>
          <div className="map-reset-actions">
            <button className="map-reset-danger" type="button" disabled={busy} onClick={() => void wipe()}>{props.t("profile.mapReset.confirm")}</button>
            <button className="secondary-button compact-button" type="button" disabled={busy} onClick={() => setConfirming(false)}>{props.t("profile.mapReset.cancel")}</button>
          </div>
          {failed && <p className="message error-message" role="alert">{props.t("profile.mapReset.error")}</p>}
        </div>
      )}
    </div>
  );
}

function formatWalkTime(total: number): string {
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

// ---- the mind map itself ----

function ProfileMap(props: {
  clientName: string;
  branches: Array<{ category: ProfileFactDto["category"]; color: string; facts: ProfileFactDto[] }>;
  csrfToken: string;
  t: (key: string) => string;
  onPointer: (point: { x: number; y: number; fact?: string }) => void;
  walkActive: boolean;
  locked?: boolean;
  onFactRemoved: (factId: string) => void;
  onFactRestored: (factId: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const centerRef = useRef<HTMLDivElement | null>(null);
  const cursorRef = useRef<HTMLDivElement | null>(null);
  const branchListRef = useRef<HTMLDivElement | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [mobileBranchIndex, setMobileBranchIndex] = useState(0);

  useEffect(() => {
    setMobileBranchIndex((current) => Math.min(current, Math.max(0, props.branches.length - 1)));
  }, [props.branches.length]);

  // Mobile reader: the horizontal grid row stretches every category to the
  // TALLEST one, so a short branch ended in ~300px of dead scroll before the
  // partner card (audit a1-r5-06). The container follows the ACTIVE branch's
  // real height instead; desktop keeps its automatic layout.
  useEffect(() => {
    const list = branchListRef.current;
    if (!list) return;
    const media = window.matchMedia("(max-width: 767px)");
    const apply = () => {
      if (!media.matches) {
        list.style.height = "";
        return;
      }
      const branch = list.children.item(mobileBranchIndex) as HTMLElement | null;
      if (branch) list.style.height = `${branch.offsetHeight}px`;
    };
    apply();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(apply);
    const active = list.children.item(mobileBranchIndex);
    if (active) observer?.observe(active);
    media.addEventListener("change", apply);
    return () => {
      observer?.disconnect();
      media.removeEventListener("change", apply);
    };
  }, [mobileBranchIndex, props.branches]);

  function selectMobileBranch(index: number) {
    const bounded = Math.max(0, Math.min(index, props.branches.length - 1));
    const list = branchListRef.current;
    const branch = list?.children.item(bounded) as HTMLElement | null;
    if (!list || !branch) return;
    setMobileBranchIndex(bounded);
    list.scrollTo({
      left: branch.offsetLeft - list.offsetLeft,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
    });
  }

  function syncMobileBranch() {
    const list = branchListRef.current;
    if (!list) return;
    const branches = [...list.children] as HTMLElement[];
    let closest = 0;
    let distance = Number.POSITIVE_INFINITY;
    for (const [index, branch] of branches.entries()) {
      const candidate = Math.abs(branch.offsetLeft - list.offsetLeft - list.scrollLeft);
      if (candidate < distance) {
        closest = index;
        distance = candidate;
      }
    }
    setMobileBranchIndex(closest);
  }

  // Strike a node straight from the map: the card folds away in place
  // (optimistic, animated, no reload, no scroll jump); the server call runs in
  // the background and a failure quietly brings the card back.
  function removeFact(factId: string, node: Element | null) {
    setRemoving(factId);
    const finalize = () => {
      setRemoving(null);
      props.onFactRemoved(factId);
    };
    const card = node instanceof HTMLElement ? node : null;
    if (card && typeof card.animate === "function" && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const height = card.offsetHeight;
      card.style.overflow = "hidden";
      const animation = card.animate(
        [
          { opacity: 1, transform: "scale(1)", height: `${height}px` },
          { opacity: 0, transform: "scale(0.92)", height: "0px", paddingTop: "0px", paddingBottom: "0px" }
        ],
        { duration: 260, easing: "ease-in" }
      );
      animation.onfinish = finalize;
      animation.oncancel = finalize;
    } else {
      finalize();
    }
    void (async () => {
      try {
        const response = await fetch(`/api/profile/facts/${encodeURIComponent(factId)}/updates`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", "X-Launchpad-CSRF": props.csrfToken },
          body: JSON.stringify({ action: "irrelevant", text: null, attachmentId: null })
        });
        if (!response.ok) throw new Error("remove_failed");
      } catch {
        props.onFactRestored(factId);
      }
    })();
  }

  // Name on top, a vertical spine down the left edge, branch pills hanging off
  // it, facts fanning out to the right (Pavel's map annotation).
  const drawLinks = useCallback(() => {
    const container = containerRef.current;
    const svg = svgRef.current;
    const center = centerRef.current;
    if (!container || !svg || !center) return;
    const base = container.getBoundingClientRect();
    svg.setAttribute("viewBox", `0 0 ${base.width} ${base.height}`);
    svg.setAttribute("width", String(base.width));
    svg.setAttribute("height", String(base.height));
    const paths: string[] = [];
    const centerRect = center.getBoundingClientRect();
    const spineX = 26;
    const cy = centerRect.bottom - base.top;
    let lowestPill = cy;
    for (const pill of container.querySelectorAll<HTMLElement>("[data-map-pill]")) {
      const color = pill.dataset.color ?? "#4a90ff";
      const rect = pill.getBoundingClientRect();
      const px = rect.left - base.left;
      const py = rect.top + rect.height / 2 - base.top;
      lowestPill = Math.max(lowestPill, py);
      paths.push(`<path d="M ${spineX} ${py} L ${px} ${py}" stroke="${color}" />`);
      const branch = pill.closest("[data-map-branch]");
      if (!branch) continue;
      const pillRight = rect.right - base.left;
      for (const card of branch.querySelectorAll<HTMLElement>("[data-map-fact]")) {
        const cardRect = card.getBoundingClientRect();
        const fx = cardRect.left - base.left;
        const fy = cardRect.top + cardRect.height / 2 - base.top;
        paths.push(`<path d="M ${pillRight} ${py} C ${pillRight + (fx - pillRight) / 2} ${py}, ${pillRight + (fx - pillRight) / 2} ${fy}, ${fx} ${fy}" stroke="${color}" stroke-opacity="0.55" />`);
      }
    }
    paths.unshift(`<path d="M ${spineX} ${cy} L ${spineX} ${lowestPill}" stroke="rgba(245, 158, 11, 0.55)" stroke-width="2" />`);
    svg.innerHTML = `<g fill="none" stroke-width="1.5">${paths.join("")}</g>`;
  }, []);

  useLayoutEffect(() => {
    drawLinks();
    const observer = new ResizeObserver(() => drawLinks());
    if (containerRef.current) observer.observe(containerRef.current);
    window.addEventListener("resize", drawLinks);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", drawLinks);
    };
  }, [drawLinks, props.branches]);

  function handleMove(event: React.PointerEvent<HTMLDivElement>) {
    moveWalkCursorOrb(cursorRef.current, props.walkActive, event.clientX, event.clientY);
    if (props.locked) return;
    const base = containerRef.current?.getBoundingClientRect();
    if (!base || base.width === 0 || base.height === 0) return;
    const fact = (event.target as HTMLElement).closest<HTMLElement>("[data-map-fact]")?.dataset.factKey;
    props.onPointer({
      x: Number(((event.clientX - base.left) / base.width).toFixed(4)),
      y: Number(((event.clientY - base.top) / base.height).toFixed(4)),
      ...(fact ? { fact } : {})
    });
  }

  return (
    <div
      className={`pmap${props.locked ? " pmap-locked" : ""}`}
      ref={containerRef}
      onPointerMove={handleMove}
      onPointerLeave={() => moveWalkCursorOrb(cursorRef.current, false, 0, 0)}
    >
      <svg className="pmap-links" ref={svgRef} aria-hidden="true" />
      <div className="pmap-name" ref={centerRef}>{props.clientName}</div>
      {props.branches.length > 1 && (
        <nav className="profile-map-reader-controls" aria-label={props.t("profile.map.navigationLabel")}>
          <button type="button" disabled={mobileBranchIndex === 0} onClick={() => selectMobileBranch(mobileBranchIndex - 1)} aria-label={props.t("profile.map.previous")}>‹</button>
          <span aria-live="polite">{props.t("profile.map.position")
            .replace("{current}", String(mobileBranchIndex + 1))
            .replace("{total}", String(props.branches.length))}</span>
          <button type="button" disabled={mobileBranchIndex === props.branches.length - 1} onClick={() => selectMobileBranch(mobileBranchIndex + 1)} aria-label={props.t("profile.map.next")}>›</button>
        </nav>
      )}
      <div className="pmap-branches" ref={branchListRef} onScroll={syncMobileBranch}>
        {props.branches.map((branch) => (
          <div className="pmap-branch" data-map-branch key={branch.category}>
            <span className="pmap-pill" data-map-pill data-color={branch.color} style={{ borderColor: branch.color, color: branch.color }}>
              {props.t(`questionnaire.factCategory.${branch.category}`)}
            </span>
            <ul className="pmap-facts">
              {branch.facts.map((fact) => {
                const sourceUrl = factSourceUrl(fact.source);
                return (
                  <li className="pmap-fact" data-map-fact data-fact-key={fact.factKey} key={fact.id} style={{ borderColor: `color-mix(in srgb, ${branch.color} 35%, transparent)` }}>
                    {!props.locked && (
                      <button
                        className="pmap-fact-remove"
                        type="button"
                        disabled={removing === fact.id}
                        aria-label={props.t("profile.factRemove")}
                        title={props.t("profile.factRemove")}
                        onClick={(event) => removeFact(fact.id, (event.currentTarget as HTMLElement).closest("[data-map-fact]"))}
                      >{"×"}</button>
                    )}
                    <p>{fact.effectiveText}</p>
                    {sourceUrl && <a className="quiet-link pmap-source" href={sourceUrl} target="_blank" rel="noreferrer">{props.t("profile.source")}</a>}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      {props.walkActive && <div ref={cursorRef} className="walk-cursor-dot" aria-hidden="true" />}
    </div>
  );
}

function factSourceUrl(source: unknown): string | null {
  if (source && typeof source === "object" && !Array.isArray(source)) {
    const url = (source as Record<string, unknown>).url;
    if (typeof url === "string" && /^https?:\/\//.test(url)) return url;
  }
  return null;
}
